import { registerPlugin } from '@capacitor/core';

/**
 * Kanal ve donanım soyutlaması.
 *
 * Uygulama iki kanalda çalışır: masaüstü tarayıcı ve Android APK (Capacitor).
 * Ekran kodu hangisinde olduğunu bilmez — buradaki fonksiyonlara sorar.
 */

export type Channel = 'desktop' | 'mobile';

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
}

function getCapacitor(): CapacitorGlobal | undefined {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor;
}

/** APK içinde mi çalışıyoruz? */
export function isNative(): boolean {
  return getCapacitor()?.isNativePlatform?.() ?? false;
}

/**
 * Hangi ekran seti yüklenecek.
 *
 * APK her zaman mobil arayüzü alır. Tarayıcıda ise ekran genişliğine
 * bakılır: bir telefondan tarayıcıyla girilirse masaüstü tabloları
 * kullanılamaz halde olurdu.
 */
export function getChannel(): Channel {
  if (isNative()) return 'mobile';
  return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
}

export function vibrate(ms = 60): void {
  navigator.vibrate?.(ms);
}

/**
 * Ekranın kapanmasını engeller — tarama vardiyası boyunca gerekli.
 * APK'da native bayrak kullanılır (MainActivity), burası tarayıcı içindir.
 */
export async function keepScreenAwake(): Promise<() => void> {
  if (isNative() || !('wakeLock' in navigator)) return () => {};

  try {
    const lock = await navigator.wakeLock.request('screen');
    return () => void lock.release().catch(() => {});
  } catch {
    return () => {};
  }
}

/* ------------------------------------------------------------------ *
 * Native eklenti                                                      *
 * ------------------------------------------------------------------ */

interface NativeInfo {
  versionCode: number;
  versionName: string;
  /** Fiziksel barkod okuyucu var mı — el terminali mi telefon mu ayrımı. */
  hasScanner: boolean;
  manufacturer: string;
  model: string;
  bridge: boolean;
}

interface KonyaxlNativePlugin {
  getInfo(): Promise<NativeInfo>;
  takeSharedImage(): Promise<{ dataUri: string | null }>;
  installApk(options: { url: string }): Promise<void>;
  shareImage(options: { dataUri: string; fileName: string; text?: string }): Promise<void>;
  exitApp(): Promise<void>;
  addListener(
    event: 'scan',
    fn: (data: { code: string; labelType: string }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
  addListener(event: 'sharedImage', fn: () => void): Promise<{ remove: () => Promise<void> }>;
  addListener(
    event: 'downloadProgress',
    fn: (data: { percent: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

/**
 * `registerPlugin` tarayıcıda da güvenle çağrılabilir; native taraf yoksa
 * çağrılar hata verir, o yüzden her kullanım `isNative()` ile korunur.
 *
 * Not: eskiden `addJavascriptInterface` kullanılıyordu ama Capacitor sayfayı
 * yüklemeye başladıktan sonra eklendiği için köprü sessizce kayboluyordu.
 * Eklenti sistemi bu yarış durumunu ortadan kaldırıyor.
 */
const KonyaxlNative = registerPlugin<KonyaxlNativePlugin>('KonyaxlNative');

/** Açılışta bir kez okunur; her çağrıda köprüye gitmeye gerek yok. */
let bilgi: NativeInfo | null = null;

export async function loadNativeInfo(): Promise<NativeInfo | null> {
  if (!isNative()) return null;
  if (bilgi) return bilgi;

  try {
    bilgi = await KonyaxlNative.getInfo();
    return bilgi;
  } catch {
    return null;
  }
}

/** Köprü gerçekten bağlı mı — tanı amaçlı. */
export function nativeBridgeReady(): boolean {
  return bilgi?.bridge === true;
}

export function hasHardwareScanner(): boolean {
  return bilgi?.hasScanner ?? false;
}

export async function getNativeVersion(): Promise<number | null> {
  const info = await loadNativeInfo();
  return info?.versionCode ?? null;
}

export function getNativeVersionName(): string | null {
  return bilgi?.versionName ?? null;
}

/** Tanı için: cihaz ve köprü durumu. */
export function getNativeSummary(): string | null {
  if (!bilgi) return null;
  return `${bilgi.manufacturer} ${bilgi.model} · v${bilgi.versionName} · okuyucu: ${bilgi.hasScanner ? 'var' : 'yok'}`;
}

/**
 * DataWedge yayınlarını dinler.
 *
 * Tarayıcıda hiçbir olay gelmez; orada keyboard-wedge dinleyicisi devrededir.
 */
export function onHardwareScan(handler: (code: string) => void): () => void {
  if (!isNative()) return () => {};

  let kaldir: (() => Promise<void>) | null = null;
  let iptal = false;

  void KonyaxlNative.addListener('scan', (data) => {
    if (data?.code) handler(data.code);
  }).then((h) => {
    if (iptal) void h.remove();
    else kaldir = h.remove;
  });

  return () => {
    iptal = true;
    void kaldir?.();
  };
}

/** Android fiziksel geri tuşu. Native taraf bunu uygulamayı kapatmadan iletir. */
export function onNativeBack(handler: () => void): () => void {
  if (!isNative()) return () => {};

  const listener = () => handler();
  window.addEventListener('konyaxl-native-back', listener);
  return () => window.removeEventListener('konyaxl-native-back', listener);
}

export async function exitApp(): Promise<void> {
  if (!isNative()) return;
  await KonyaxlNative.exitApp();
}

/* ------------------------------------------------------------------ *
 * Paylaşımdan gelen resim                                             *
 * ------------------------------------------------------------------ */

/**
 * WhatsApp gibi uygulamalardan "Paylaş" ile gönderilen resmi alır.
 * Bir kez alınır ve kuyruktan düşer — aynı fotoğraf iki kez taranmaz.
 */
export async function takeSharedImage(): Promise<Blob | null> {
  if (!isNative()) return null;

  try {
    const { dataUri } = await KonyaxlNative.takeSharedImage();
    if (!dataUri) return null;

    const [bas, veri] = dataUri.split(',');
    if (!veri) return null;

    const tip = /data:([^;]+)/.exec(bas ?? '')?.[1] ?? 'image/jpeg';
    const ikili = atob(veri);
    const dizi = new Uint8Array(ikili.length);
    for (let i = 0; i < ikili.length; i++) dizi[i] = ikili.charCodeAt(i);

    return new Blob([dizi], { type: tip });
  } catch {
    return null;
  }
}

/** Paylaşım geldiğinde tetiklenir — uygulama açıkken de çalışır. */
export function onSharedImage(handler: () => void): () => void {
  if (!isNative()) return () => {};

  let kaldir: (() => Promise<void>) | null = null;
  let iptal = false;

  void KonyaxlNative.addListener('sharedImage', handler).then((h) => {
    if (iptal) void h.remove();
    else kaldir = h.remove;
  });

  return () => {
    iptal = true;
    void kaldir?.();
  };
}

/**
 * Görseli sistem paylaşım penceresiyle gönderir (yalnızca APK).
 *
 * WebView'de `navigator.share` ve indirme bağlantısı çalışmaz; ikisi de
 * tarayıcı özelliği. Paylaşım native tarafta yapılmak zorunda.
 */
export async function shareImageNative(
  dataUri: string,
  fileName: string,
  text?: string,
): Promise<void> {
  if (!isNative()) throw new Error('Native paylaşım yalnızca uygulamada çalışır');
  await KonyaxlNative.shareImage({ dataUri, fileName, text });
}

/* ------------------------------------------------------------------ *
 * Güncelleme                                                          *
 * ------------------------------------------------------------------ */

/**
 * Yeni APK'yı indirir ve Android'in kurulum ekranını açar.
 *
 * Kurulumu uygulama değil Android yapar: kullanıcı sistem ekranında
 * "Yükle" der. İlk seferde "bilinmeyen kaynaklardan yüklemeye izin ver"
 * onayı da istenir — bu Android'in güvenlik gereği, atlanamaz.
 */
export async function installApk(
  url: string,
  onProgress?: (yuzde: number) => void,
): Promise<void> {
  if (!isNative()) {
    // Tarayıcıda APK kurulamaz; dosyayı indirmekle yetinilir.
    window.open(url, '_blank');
    return;
  }

  const dinleyici = onProgress
    ? await KonyaxlNative.addListener('downloadProgress', (d) => onProgress(d.percent))
    : null;

  try {
    await KonyaxlNative.installApk({ url });
  } finally {
    await dinleyici?.remove();
  }
}
